import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  OPTICS_FRAMEWORK_2026,
  OPTICS_LENS_LABELS,
  OPTICS_LENS_DESCRIPTIONS,
  type OpticsLens,
} from '../../types/optics2026';
import type { ReviewResponseEntry, SelfNarrative } from '../../types/magicLink';
import ScoreRadioGroup, { DEFAULT_OPTICS_LEGEND } from '../ui/ScoreRadioGroup';
import BottomSheet from '../ui/BottomSheet';
import Button from '../ui/Button';

/**
 * OpticsScoringForm — reusable OPTICS 2026 scoring surface.
 *
 * Used by both the public magic-link path (/r/:token) and the login-based
 * self-review path (/self-review). Parent owns entries/selfNarrative state
 * and submission; this component renders the scoring UI.
 *
 * Audit fixes applied in this rebuild:
 *   #5  Evidence + dev-action collapsed behind "+ Add evidence" link per area
 *   #7  Persistent "Submit review" CTA when 100% complete, from any section
 *   #8  Auto-save label rendered next to progress bar (parent supplies the text)
 *   #13 Score legend visible above the first score row of each area
 *   #23 Score buttons are a real radiogroup (see ScoreRadioGroup primitive)
 *   #28 Focus reset to section heading on Next/Back nav (screen-reader cue)
 *
 * Mobile additions:
 *   - Sticky bottom action bar for Back / Next / Submit
 *   - Section navigator collapses into a chevron-dropdown bottom-sheet picker
 *     (full horizontal tab strip retained at sm: and up)
 */

export interface OpticsScoringFormProps {
  entries: Record<string, ReviewResponseEntry>;
  setEntries: React.Dispatch<React.SetStateAction<Record<string, ReviewResponseEntry>>>;
  selfNarrative?: SelfNarrative;
  setSelfNarrative?: React.Dispatch<React.SetStateAction<SelfNarrative>>;
  isSelf: boolean;
  onSubmit: () => void | Promise<void>;
  submitting?: boolean;
  submitLabel?: string;
  errorMsg?: string;
  /**
   * Optional auto-save indicator string — rendered in the progress strip.
   * Pass `null` / undefined to hide. Use the `useAutoSave` hook in the parent.
   */
  autoSaveLabel?: string | null;
}

const SELF_NARRATIVE_FIELDS = [
  { key: 'deliveredIndividually', label: 'What I delivered individually' },
  { key: 'supportedTeam', label: 'How I supported my team' },
  { key: 'contributedToCompany', label: 'How I contributed to the company' },
  { key: 'needSupport', label: 'Where I need support' },
] as const;

const OpticsScoringForm: React.FC<OpticsScoringFormProps> = ({
  entries,
  setEntries,
  selfNarrative,
  setSelfNarrative,
  isSelf,
  onSubmit,
  submitting = false,
  submitLabel = 'Submit review',
  errorMsg,
  autoSaveLabel,
}) => {
  const [currentValueIdx, setCurrentValueIdx] = useState(0);
  const [sectionPickerOpen, setSectionPickerOpen] = useState(false);
  const [expandedEvidence, setExpandedEvidence] = useState<Set<string>>(new Set());
  const sectionTitleRef = useRef<HTMLHeadingElement>(null);

  const value = OPTICS_FRAMEWORK_2026[currentValueIdx];
  const isLastSection = currentValueIdx === OPTICS_FRAMEWORK_2026.length - 1;

  // ─── Derived state ──────────────────────────────────────────────────────
  const totalLensScores = useMemo(
    () => OPTICS_FRAMEWORK_2026.reduce((s, v) => s + v.areas.length * 3, 0),
    [],
  );

  const filledScores = useMemo(() => {
    let n = 0;
    for (const e of Object.values(entries)) {
      (['ind', 'team', 'co'] as OpticsLens[]).forEach(l => {
        if (typeof e.scores[l] === 'number' && (e.scores[l] as number) > 0) n++;
      });
    }
    return n;
  }, [entries]);

  const completionPct = Math.round((filledScores / totalLensScores) * 100);
  const allScoresComplete = filledScores === totalLensScores;

  const isAreaComplete = (areaId: string): boolean => {
    const e = entries[areaId];
    if (!e) return false;
    return (['ind', 'team', 'co'] as OpticsLens[]).every(
      l => typeof e.scores[l] === 'number' && (e.scores[l] as number) > 0,
    );
  };

  const isValueComplete = (vIdx: number): boolean =>
    OPTICS_FRAMEWORK_2026[vIdx].areas.every(a => isAreaComplete(a.id));

  // ─── Mutators ───────────────────────────────────────────────────────────
  const setScore = (areaId: string, lens: OpticsLens, score: number) => {
    setEntries(prev => {
      const existing = prev[areaId] ?? { areaId, scores: {} };
      return {
        ...prev,
        [areaId]: { ...existing, scores: { ...existing.scores, [lens]: score } },
      };
    });
  };

  const setText = (areaId: string, field: 'evidence' | 'developmentAction', text: string) => {
    setEntries(prev => {
      const existing = prev[areaId] ?? { areaId, scores: {} };
      return { ...prev, [areaId]: { ...existing, [field]: text } };
    });
  };

  const toggleEvidence = (areaId: string) => {
    setExpandedEvidence(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
  };

  // ─── Section navigation ─────────────────────────────────────────────────
  const goToSection = (idx: number) => {
    setCurrentValueIdx(idx);
    setSectionPickerOpen(false);
    // Scroll back to top — sectionTitleRef receives focus via effect below
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Focus the section heading on change so screen readers announce the new
  // section context. (Audit #28.)
  useEffect(() => {
    sectionTitleRef.current?.focus({ preventScroll: true });
  }, [currentValueIdx]);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <>
      {/* Progress strip */}
      <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-3 md:mb-4">
        <div className="flex justify-between items-center gap-3 text-sm text-stone-500 mb-2">
          <span className="truncate">
            Section {currentValueIdx + 1} of {OPTICS_FRAMEWORK_2026.length} · {value.title}
          </span>
          <span className="shrink-0 tabular-nums">
            {completionPct}%
            {autoSaveLabel && <span className="ml-2 text-emerald-700">· {autoSaveLabel}</span>}
          </span>
        </div>
        <div
          className="h-1 bg-stone-100 rounded-full overflow-hidden"
          role="progressbar"
          aria-valuenow={completionPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Review completion"
        >
          <div className="h-full bg-stone-900 transition-all" style={{ width: `${completionPct}%` }} />
        </div>

        {/* Persistent "Submit review" CTA when all scores complete (audit #7).
         *  Self-reviewers must still complete Section 7 narrative — show only
         *  for non-self when 100%. */}
        {allScoresComplete && !isSelf && (
          <div className="mt-3 pt-3 border-t border-stone-100 flex items-center justify-between gap-3">
            <span className="text-sm text-emerald-700 font-medium">
              All sections complete
            </span>
            <Button
              variant="submit"
              icon="check"
              onClick={() => void onSubmit()}
              loading={submitting}
            >
              {submitLabel}
            </Button>
          </div>
        )}
      </div>

      {/* Section navigator — mobile chevron-dropdown */}
      <div className="sm:hidden mb-3">
        <button
          type="button"
          onClick={() => setSectionPickerOpen(true)}
          className="w-full h-11 px-4 bg-white border border-stone-200 rounded-xl flex items-center justify-between gap-3 hover:border-stone-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-stone-500"
          aria-haspopup="dialog"
          aria-expanded={sectionPickerOpen}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="w-6 h-6 rounded-full bg-stone-900 text-white text-xs font-bold flex items-center justify-center shrink-0">
              {value.code}
            </span>
            <span className="text-base font-semibold text-stone-900 truncate">{value.title}</span>
          </span>
          <span className="flex items-center gap-1 shrink-0">
            <span className="text-sm text-stone-500 tabular-nums">
              {currentValueIdx + 1}/{OPTICS_FRAMEWORK_2026.length}
            </span>
            <span className="material-symbols-rounded text-stone-500" style={{ fontSize: 20 }} aria-hidden="true">
              expand_more
            </span>
          </span>
        </button>
        <p className="text-xs text-stone-500 mt-1.5 text-center">Tap to jump to another section</p>
      </div>

      {/* Section navigator — desktop horizontal tabs */}
      <nav
        className="hidden sm:flex gap-1.5 mb-4 overflow-x-auto pb-1 scroll-fade-x"
        aria-label="OPTICS sections"
      >
        {OPTICS_FRAMEWORK_2026.map((v, i) => {
          const done = isValueComplete(i);
          const active = i === currentValueIdx;
          return (
            <button
              key={v.code}
              type="button"
              onClick={() => goToSection(i)}
              aria-current={active ? 'page' : undefined}
              className={[
                'px-3 h-9 rounded-md text-xs font-medium whitespace-nowrap transition-colors shrink-0',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-stone-500',
                active
                  ? 'bg-stone-900 text-white'
                  : done
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200',
              ].join(' ')}
            >
              <b>{v.code}</b> · {v.title}
              {done && !active && (
                <span
                  className="material-symbols-rounded ml-1 align-text-bottom"
                  style={{ fontSize: 14 }}
                  aria-hidden="true"
                >
                  check
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Current value card */}
      <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">
          OPTICS Value · {value.code}
        </div>
        <h2
          ref={sectionTitleRef}
          tabIndex={-1}
          className="text-2xl font-semibold text-stone-900 tracking-tight mb-2 focus:outline-none"
        >
          {value.title}
        </h2>
        <p className="text-base text-stone-700 leading-relaxed mb-6 max-w-2xl md:text-sm md:text-stone-600">
          {value.intent}
        </p>

        {value.areas.map((area, idx) => {
          const entry = entries[area.id] ?? { areaId: area.id, scores: {} };
          const evidenceOpen =
            expandedEvidence.has(area.id) || Boolean(entry.evidence || entry.developmentAction);
          return (
            <section key={area.id} className={idx > 0 ? 'pt-6 mt-6 border-t border-stone-100' : ''}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-sm font-semibold text-stone-500">{area.letter}.</span>
                <h3 className="text-base font-semibold text-stone-900">{area.title}</h3>
              </div>
              <ul className="text-sm text-stone-600 leading-relaxed mb-4 ml-5 list-decimal list-outside space-y-1">
                {area.behaviors.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>

              {/* Score legend — visible above first score row (audit #13) */}
              {idx === 0 && (
                <div className="ml-5 mb-3 bg-stone-50 border border-stone-100 rounded-xl p-3">
                  <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Score legend</div>
                  <div className="grid grid-cols-5 gap-2 text-center">
                    {[1, 2, 3, 4, 5].map(n => (
                      <div key={n}>
                        <div className="text-base font-semibold text-stone-900 tabular-nums">{n}</div>
                        <div className="text-xs text-stone-500 leading-tight mt-0.5">
                          {DEFAULT_OPTICS_LEGEND[n]}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Three lens score rows */}
              <div className="ml-5 space-y-2">
                {(['ind', 'team', 'co'] as OpticsLens[]).map(lens => (
                  <ScoreRadioGroup
                    key={lens}
                    label={OPTICS_LENS_LABELS[lens]}
                    description={OPTICS_LENS_DESCRIPTIONS[lens]}
                    value={entry.scores[lens]}
                    onChange={n => setScore(area.id, lens, n)}
                  />
                ))}
              </div>

              {/* Collapsible evidence + dev-action (audit #5) */}
              <div className="ml-5 mt-3">
                {evidenceOpen ? (
                  <div className="space-y-2">
                    <div>
                      <label
                        htmlFor={`evidence-${area.id}`}
                        className="block text-sm font-medium text-stone-700 mb-1"
                      >
                        Evidence <span className="text-stone-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        id={`evidence-${area.id}`}
                        placeholder="Specific, observable behaviours during this review period…"
                        value={entry.evidence ?? ''}
                        onChange={e => setText(area.id, 'evidence', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-base text-stone-900 border border-stone-300 rounded-xl resize-y placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`devaction-${area.id}`}
                        className="block text-sm font-medium text-stone-700 mb-1"
                      >
                        Development action <span className="text-stone-400 font-normal">(optional)</span>
                      </label>
                      <textarea
                        id={`devaction-${area.id}`}
                        placeholder="Most practical next step…"
                        value={entry.developmentAction ?? ''}
                        onChange={e => setText(area.id, 'developmentAction', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 text-base text-stone-900 border border-stone-300 rounded-xl resize-y placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleEvidence(area.id)}
                    className="text-base text-blue-600 underline underline-offset-2 hover:text-blue-700 md:text-sm md:no-underline md:hover:underline"
                  >
                    + Add evidence (optional)
                  </button>
                )}
              </div>
            </section>
          );
        })}

        {/* Inline action row — desktop only. Mobile uses sticky bottom bar below. */}
        <div className="mt-7 pt-5 border-t border-stone-200 hidden sm:flex justify-between items-center">
          <span className="text-sm text-emerald-700 flex items-center gap-1.5">
            <span className="material-symbols-rounded" style={{ fontSize: 16 }} aria-hidden="true">
              check_circle
            </span>
            Auto-saved · close &amp; resume anytime
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              icon="arrow_back"
              onClick={() => setCurrentValueIdx(c => Math.max(0, c - 1))}
              disabled={currentValueIdx === 0}
            >
              Back
            </Button>
            {!isLastSection ? (
              <Button
                variant="primary"
                icon="arrow_forward"
                iconPosition="trailing"
                onClick={() => setCurrentValueIdx(c => c + 1)}
              >
                Next: {OPTICS_FRAMEWORK_2026[currentValueIdx + 1].title}
              </Button>
            ) : (
              !isSelf && (
                <Button
                  variant="submit"
                  icon="check"
                  onClick={() => void onSubmit()}
                  loading={submitting}
                >
                  {submitLabel}
                </Button>
              )
            )}
          </div>
        </div>
      </div>

      {/* Self-only Section 7 narrative */}
      {isSelf && isLastSection && setSelfNarrative && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 md:p-6 mt-4">
          <div className="text-xs uppercase tracking-wider text-stone-500 mb-1">
            Self-Review · Section 7
          </div>
          <h3 className="text-lg font-semibold text-stone-900 mb-1 tracking-tight">Your reflection</h3>
          <p className="text-sm text-stone-600 mb-5 leading-relaxed">
            Short, honest examples. Focus on what you personally delivered, how you supported your team,
            how your work helped the company, and where you still need to grow.
          </p>

          {SELF_NARRATIVE_FIELDS.map(f => {
            const fieldId = `narrative-${f.key}`;
            return (
              <div key={f.key} className="mb-4">
                <label htmlFor={fieldId} className="block text-sm font-medium text-stone-700 mb-1">
                  {f.label}
                </label>
                <textarea
                  id={fieldId}
                  value={selfNarrative?.[f.key] ?? ''}
                  onChange={e =>
                    setSelfNarrative(prev => ({ ...(prev ?? {}), [f.key]: e.target.value }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 text-base text-stone-900 border border-stone-300 rounded-xl resize-y placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:border-transparent"
                />
              </div>
            );
          })}

          <div className="pt-4 border-t border-stone-200 flex justify-end">
            <Button variant="submit" icon="check" onClick={() => void onSubmit()} loading={submitting}>
              {submitLabel}
            </Button>
          </div>
        </div>
      )}

      {/* Inline error message */}
      {errorMsg && (
        <div
          className="mt-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 flex items-start gap-2"
          role="alert"
        >
          <span className="material-symbols-rounded text-red-600 shrink-0" style={{ fontSize: 20 }} aria-hidden="true">
            error
          </span>
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Sticky bottom action bar — mobile only */}
      <div
        className="sticky bottom-0 -mx-4 mt-4 px-4 py-3 bg-white/95 backdrop-blur border-t border-stone-200 flex items-center justify-between gap-2 sm:hidden"
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
      >
        <Button
          variant="tertiary"
          icon="arrow_back"
          onClick={() => setCurrentValueIdx(c => Math.max(0, c - 1))}
          disabled={currentValueIdx === 0}
        >
          Back
        </Button>
        {!isLastSection ? (
          <Button
            variant="primary"
            icon="arrow_forward"
            iconPosition="trailing"
            onClick={() => setCurrentValueIdx(c => c + 1)}
          >
            Next
          </Button>
        ) : isSelf ? (
          <span className="text-xs text-stone-500">Scroll for narrative ↓</span>
        ) : (
          <Button variant="submit" icon="check" onClick={() => void onSubmit()} loading={submitting}>
            {submitLabel}
          </Button>
        )}
      </div>

      {/* Section picker — mobile bottom sheet */}
      <BottomSheet
        open={sectionPickerOpen}
        onClose={() => setSectionPickerOpen(false)}
        title="Jump to section"
        description="Each section can be completed in any order. Progress is saved automatically."
      >
        <ul className="py-2">
          {OPTICS_FRAMEWORK_2026.map((v, i) => {
            const done = isValueComplete(i);
            const active = i === currentValueIdx;
            const sectionFilled = v.areas.reduce(
              (s, a) =>
                s +
                (['ind', 'team', 'co'] as OpticsLens[]).filter(
                  l => typeof entries[a.id]?.scores[l] === 'number',
                ).length,
              0,
            );
            const sectionTotal = v.areas.length * 3;
            return (
              <li key={v.code}>
                <button
                  type="button"
                  onClick={() => goToSection(i)}
                  className={[
                    'w-full text-left px-5 py-3 flex items-center gap-3 min-h-14',
                    'hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-stone-500',
                    active ? 'bg-stone-50' : '',
                  ].join(' ')}
                  aria-current={active ? 'page' : undefined}
                >
                  <span
                    className={[
                      'w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0',
                      done
                        ? 'bg-emerald-500 text-white'
                        : active
                          ? 'bg-stone-900 text-white'
                          : 'bg-stone-200 text-stone-600',
                    ].join(' ')}
                  >
                    {done ? (
                      <span className="material-symbols-rounded" style={{ fontSize: 18 }} aria-hidden="true">
                        check
                      </span>
                    ) : (
                      v.code
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-base font-medium text-stone-900">{v.title}</span>
                    <span className="block text-sm text-stone-500 tabular-nums">
                      {sectionFilled} / {sectionTotal} lens scores
                    </span>
                  </span>
                  {active && (
                    <span className="text-xs text-stone-500 font-medium shrink-0">Current</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </>
  );
};

export default OpticsScoringForm;
